'use client'

import type React from 'react'
import type { FeaturesProps } from './types'
import { FeaturesSplit } from './features-split'
import { FeaturesShowcase } from './features-showcase'
import { FeaturesSticky } from './features-sticky'
import { FeaturesGallery } from './features-gallery'
import { FeaturesCodeCards } from './features-code-cards'
import { FeaturesSplitCode } from './features-split-code'
import { FeaturesGrid } from './grid'
import { Stats } from './stats'
import { FeaturesCodeTabs } from './features-code-tabs'
import { FeaturesCodeCardSingle } from './features-code-card-single'

export type { FeaturesProps }

// =============================================================================
// Main Features Router
// =============================================================================

/**
 * Features - Adaptive features section component
 *
 * Routes to the appropriate variant component based on the `variant` discriminant.
 * Each case performs minimal inline transforms to bridge between the CMS-facing
 * FeaturesProps interface and the internal component props.
 *
 * @example
 * ```tsx
 * // Grid layout
 * <Features
 *   variant="grid"
 *   title="Our Features"
 *   features={[
 *     { title: 'Fast', description: 'Lightning quick' },
 *     { title: 'Secure', description: 'Bank-level security' },
 *   ]}
 * />
 *
 * // Split layout with image
 * <Features
 *   variant="right"
 *   title="Platform Features"
 *   description="Everything you need"
 *   features={features}
 *   image={{ src: '/image.png', alt: 'Feature image' }}
 * />
 * ```
 */
export function Features(props: FeaturesProps): React.ReactElement | null {
  switch (props.variant) {
    // ─── Grid Family ──────────────────────────────────────────────────────────

    case 'grid':
      return (
        <FeaturesGrid
          title={props.title}
          features={props.features?.map((f) => ({ heading: f.title, description: f.description, icon: f.icon }))}
          columns={props.columns}
          className={props.className}
        />
      )

    // ─── Split Family ─────────────────────────────────────────────────────────

    case 'right':
    case 'left':
      return (
        <FeaturesSplit
          position={props.variant}
          heading={props.title}
          description={props.description}
          features={props.features?.map((f) => ({ icon: f.icon, text: f.title }))}
          button={props.button}
          learnMore={props.learnMore}
          image={props.image}
          mediaType={props.mediaType}
          codeWindow={props.codeWindow}
          className={props.className}
        />
      )

    // ─── Showcase / Sticky ────────────────────────────────────────────────────

    case 'showcase':
      return (
        <FeaturesShowcase
          heading={props.title}
          description={props.description}
          features={props.features?.map((f) => ({
            title: f.title,
            description: f.description,
            bullets: f.bullets,
            button: f.button,
            learnMore: f.learnMore,
            image: f.image,
            codeWindow: f.code ? { code: f.code, language: f.language ?? 'typescript' } : undefined,
          }))}
          mediaType={props.mediaType}
          className={props.className}
        />
      )

    case 'sticky':
      return (
        <FeaturesSticky
          heading={props.title}
          description={props.description}
          features={props.features?.map((f) => ({
            tagline: f.title,
            description: f.description,
            buttonText: f.button?.text,
            buttonUrl: f.button?.url,
          }))}
          className={props.className}
        />
      )

    // ─── Code-focused ─────────────────────────────────────────────────────────

    case 'gallery':
      return (
        <FeaturesGallery
          heading={props.title}
          description={props.description}
          content1={props.content1}
          content2={props.content2}
          stackedCode={props.stackedCode}
          tallCode={props.tallCode}
          className={props.className}
        />
      )

    case 'code-cards':
      return (
        <FeaturesCodeCards
          label={props.label}
          heading={props.title}
          description={props.description}
          columns={props.columns}
          cards={props.cards}
          className={props.className}
        />
      )

    case 'split-code':
      return (
        <FeaturesSplitCode
          heading={props.title}
          description={props.description}
          features={props.features}
          primaryButton={props.primaryButton}
          secondaryButton={props.secondaryButton}
          buttonPosition={props.buttonPosition}
          code={props.code}
          className={props.className}
        />
      )

    // ─── New Variants ─────────────────────────────────────────────────────────

    case 'stats':
      return <Stats heading={props.title} description={props.description} stats={props.stats ?? []} className={props.className} />

    case 'code-card-single':
      return (
        <FeaturesCodeCardSingle
          heading={props.title ?? ''}
          description={props.description ?? ''}
          button={props.primaryButton}
          secondaryButton={props.secondaryButton}
          installSnippet={props.installSnippet}
          code={props.codeCard ?? { code: '', language: 'typescript' }}
          className={props.className}
        />
      )

    case 'code-tabs':
      return (
        <FeaturesCodeTabs
          heading={props.title}
          description={props.description}
          tabs={
            props.tabs?.map((t) => ({
              label: t.label,
              icon: t.icon,
              title: t.title,
              description: t.description,
              bullets: t.bullets,
              code: t.code,
              button: t.button,
            })) ?? []
          }
          className={props.className}
        />
      )

    default: {
      void (props satisfies never)
      return null
    }
  }
}

export default Features
